import React, {useEffect, useMemo, useState} from "react";
import {LoggedInView} from "../common/LoggedInView";
import {Link, useLocation, useParams} from "react-router-dom";
import Breadcrumbs from "../common/Breadcrumbs";
import ElogInput from "../common/form/ElogInput";
import ElogSelect, {Option} from "../common/form/ElogSelect";
import ElogCheckbox from "../common/form/ElogCheckbox";
import {
    updateVendorContact,
    resetUpdateVendorContactStatus,
    getCurrentVendorContact, getVendorContacts
} from "../../store/reducers/VendorContactsSlice";
import {RequestStatus} from "../../utils/Enums";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import ElogTextarea from "../common/form/ElogTextarea";
import CategoryType from "../../models/CategoryType";
import {apiClient} from "../../utils/ApiClient";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import {getCurrentVendor} from "../../store/reducers/VendorsSlice";
import {FormikProvider, useFormik, Field, Form} from "formik";
import {updateClient} from "../../store/reducers/ClientsSlice";
import WhatsAppInput from "./components/WhatsAppInput";
import TeamsInput from "./components/TeamsInput";
import Category from "../../models/Category";
import {EXCLUSION_CATEGORY_TYPE} from "../../utils/Constants";
import GroupTable from "../clientContacts/components/GroupTable";
import {getCountries} from "../../store/reducers/CountriesSlice";
import ReplacementsTable from "./components/ReplacementsTable";
import {getServices} from "../../store/reducers/ServicesSlice";
import HitsTable from "./components/HitsTable";
import RemovedRoutesTable from "./components/RemovedRoutesTable";

interface ModifyVendorContactScreenProps {
}

export interface ModifyVendorContactFormProps {
    name: string;
    plea: string;
    email: string;
    phone: string;
    comment?: string;
    banned: boolean;
    groups: string[];
}

export const ModifyVendorContactScreen: React.FC<ModifyVendorContactScreenProps> = () => {

    const dispatch = useAppDispatch();
    const location = useLocation();

    const {vendorId, contactId} = useParams<{ vendorId: string; contactId: string }>();

    const vendorContactsState = useAppSelector((state) => state.vendorContacts);
    const vendorsState = useAppSelector((state) => state.vendors);

    const requestStatus: RequestStatus = vendorContactsState.updateVendorContactStatus;

    useEffect(() => {
        dispatch(getServices(false));
    }, []);

    const breadcrumbLinks: BreadcrumbLink[] = [
        {text: "Hankijad", url: "/vendors"},
        {text: "Hankija kontaktid", url: `/vendors/${vendorId}/contacts`},
        {text: "Muuda hankija kontakti", url: `/vendors/${vendorId}/contacts/${contactId}/modify`},
    ];

    const languageOptions: Option[] = [
        {value: 'est', label: 'Eesti'},
        {value: 'eng', label: 'Inglise'},
        //{value: 'fin', label: 'Soome'},
        //{value: 'swe', label: 'Rootsi'},
    ];

    const [categoryTypes, setCategoryTypes] = useState<CategoryType[]>([]);
    const [exclusionCategories, setExclusionCategories] = useState<Category[]>([]);

    useEffect(() => {
        if (contactId && vendorId) {
            dispatch(getCurrentVendor({ id: vendorId }));
            dispatch(getCurrentVendorContact({vendorId: parseInt(vendorId), contactId: parseInt(contactId)}));
            dispatch(getVendorContacts({ vendorId: parseInt(vendorId, 10) }));
            dispatch(getCountries());
        }
    }, [contactId]);

    useEffect(() => {
        dispatch(resetUpdateVendorContactStatus());
    }, [location]);

    useEffect(() => {

        apiClient.get("category-types/all-by-vendor/" + vendorId).then((response) => {
            setCategoryTypes(response.data);
        });

        apiClient.get("category-types/all-by-category").then((response) => {

            response.data.map((categoryType: CategoryType) => {

                if ( categoryType.id === EXCLUSION_CATEGORY_TYPE ) {
                    setExclusionCategories(categoryType?.categories ?? []);
                }

            })

        });

    }, [vendorId]);

    const selectedCategoryIds = useMemo(() => {
        return vendorContactsState.currentVendorContact?.categories?.map((category) => String(category.id));
    }, [vendorContactsState.currentVendorContact]);

    const selectedExcludexCategoryIds = useMemo(() => {
        return vendorContactsState.currentVendorContact?.excludes?.map((category) => String(category.id));
    }, [vendorContactsState.currentVendorContact]);

    // @ts-ignore
    const formik = useFormik({
        initialValues: {
            name: vendorContactsState?.currentVendorContact?.name ?? "",
            plea: vendorContactsState?.currentVendorContact?.plea ?? "",
            emails: vendorContactsState?.currentVendorContact?.emails ?? [''],
            phone: vendorContactsState?.currentVendorContact?.phone ?? "",
            whatsapp: vendorContactsState?.currentVendorContact?.whatsapp ?? "",
            skype: vendorContactsState?.currentVendorContact?.skype ?? "",
            comment: vendorContactsState?.currentVendorContact?.comment ?? "",
            language: vendorContactsState?.currentVendorContact?.language ?? "est",
            banned: vendorContactsState?.currentVendorContact?.banned_at ?? false,
            removed: vendorContactsState?.currentVendorContact?.removed_at ?? false,
            groups: selectedCategoryIds,
            exclude: selectedExcludexCategoryIds,
            hits: vendorContactsState?.currentVendorContact?.hits ?? [],
            noOffer: vendorContactsState?.currentVendorContact?.no_offers ?? [],
            replacements: vendorContactsState?.currentVendorContact?.replacements?.map(replacement => ({
                ...replacement,
                replacementId: replacement.replacement_vendor_contact_id
            })) ?? []
        },
        onSubmit: (values: any) => {
            if (vendorId && contactId) {
                dispatch(updateVendorContact({
                    vendorId: parseInt(vendorId, 10),
                    contactId: parseInt(contactId, 10),
                    vendorContactData: values
                }));
            }
        },
        enableReinitialize: true
    });

    const {values, setFieldValue, handleSubmit} = formik;

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks}/>
            <h1>Hankija {vendorsState?.currentVendor?.name} kontaktisiku {vendorContactsState?.currentVendorContact?.name} muutmine</h1>
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Kontakti andmed uuendatud</h2>
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Failed && vendorContactsState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {vendorContactsState.errors.map((error, index) => (
                            <span key={index}>{error}<br/></span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <div className="form">
                        <h2>
                            <span>Hankija kontakti andmed</span>
                        </h2>
                        <table className="form">
                            <tbody>
                            <ElogInput label="Nimi" name="name" mandatory={true}/>
                            <ElogInput label="Pöördumine" name="plea" mandatory={true}/>
                            {values.emails.map((email, index) => (
                                <>
                                    <ElogInput
                                        label={`E-mail ${index + 1}`}
                                        name={`emails.${index}`}
                                        type="email"
                                        mandatory={true}
                                        button={index > 0 && (
                                            <button
                                                style={{color: 'red'}}
                                                type="button"
                                                onClick={() => setFieldValue('emails', values.emails.filter((_, i) => i !== index))}
                                            >
                                                [X]
                                            </button>
                                        )}
                                    />
                                </>
                            ))}
                            <tr>
                                <th>
                                </th>
                                <td>
                                    <button
                                        type="button"
                                        onClick={() => setFieldValue('emails', [...values.emails, ''])}
                                    >
                                        Lisa
                                    </button>
                                </td>
                            </tr>
                            <ElogInput label="Telefon" name="phone" mandatory={true}/>
                            <TeamsInput label="Teams" name="skype" mandatory={false}/>
                            <WhatsAppInput label="Whatsapp" name="whatsapp" mandatory={false}/>
                            <ElogTextarea label="Kommentaar" name="comment"/>
                            <ElogSelect label="Keel" name="language" options={languageOptions} mandatory={true} />
                            <ElogCheckbox label="Keelatud" name="banned"/>
                            <ElogCheckbox label="Eemalda igalt poolt soovitustest" name="removed"/>
                            </tbody>
                        </table>
                    </div>

                    {categoryTypes.map((categoryType) => (
                        <div key={categoryType.id} className="form">
                            <h2>
                                <span>{categoryType.name}</span>
                            </h2>
                            {categoryType?.categories?.map((category) => (
                                <Field
                                    key={category.id}
                                    name="groups"
                                    type="checkbox"
                                    value={String(category.id)}
                                    render={(props: { field: { value: any; onChange: (e: React.ChangeEvent<any>) => void; onBlur: () => void; name: string } }) => (
                                        <>
                                            <label htmlFor={`group_${category.id}`}>{category.name}</label>
                                            <input
                                                {...props.field}
                                                id={`group_${category.id}`}
                                                type="checkbox"
                                                onChange={(event) => props.field.onChange(event)}
                                                onBlur={() => undefined}
                                            />
                                        </>
                                    )}
                                />
                            ))}
                        </div>
                    ))}
                    {/*
                    <br /><hr />
                    <div className="form">
                        <h2>
                            <span>Välistused</span>
                        </h2>
                        {exclusionCategories?.map((category) => (
                            <Field
                                key={'exclude' + category.id}
                                name="exclude"
                                type="checkbox"
                                value={String(category.id)}
                                render={(props: { field: { value: any; onChange: (e: React.ChangeEvent<any>) => void; onBlur: () => void; name: string } }) => (
                                    <>
                                        <label htmlFor={`exclude_${category.id}`}>{category.name}</label>
                                        <input
                                            {...props.field}
                                            id={`exclude_${category.id}`}
                                            type="checkbox"
                                            onChange={(event) => props.field.onChange(event)}
                                            onBlur={() => undefined}
                                        />
                                    </>
                                )}
                            />
                        ))}
                    </div>
                    */}

                    <HitsTable name={'hits'} label={'Hitihoiatused'} />

                    <ReplacementsTable
                        name={'replacements'}
                        label={'Asendused'}
                        contacts={vendorContactsState.vendorContacts.filter(contact => contact.id !== (contactId ? parseInt(contactId) : undefined))}
                    />

                    <RemovedRoutesTable
                        vendorId={parseInt(vendorId ?? '0')}
                        contactId={parseInt(contactId ?? '0')}
                        routes={vendorContactsState?.currentVendorContact?.routes ?? []}
                        refreshRoutes={() => {
                            dispatch(
                                getCurrentVendorContact({
                                    vendorId: parseInt(vendorId ?? '0'),
                                    contactId: parseInt(contactId ?? '0'),
                                })
                            );
                        }}
                    />

                    {requestStatus === RequestStatus.Success && (
                        <div className="message msg-ok">
                            <div>
                                <h2>Kontakti andmed uuendatud</h2>
                            </div>
                        </div>
                    )}
                    {requestStatus === RequestStatus.Failed && vendorContactsState.errors.length > 0 && (
                        <div className="message msg-error">
                            <div>
                                <h2>Viga:</h2>
                                {vendorContactsState.errors.map((error, index) => (
                                    <span key={index}>{error}<br/></span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="action clear">
                        <p>
                            <button type="submit">
                                    <span>
                                        <strong>Muuda</strong>
                                    </span>
                            </button>
                            <Link to={`/vendors/${vendorId}/contacts`}>Tühista</Link>
                        </p>
                    </div>
                </Form>
            </FormikProvider>
        </LoggedInView>
    );
};